import React, {
  useEffect, useRef, useState,
} from 'react';
import { KatModal, KatProgress } from '@amzn/katal-react';
import { SuButton } from '@amzn/seller-university-ui-components';
import {
  useStartAssessmentMutation,
  useSaveAnswerMutation,
  useSubmitAssessmentMutation,
} from 'src/redux/slices/api/su';
import { SubmitAssessmentPOSTResponse } from 'src/types/api/assessment';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';

export interface AssessmentOverlayCopy {
  title: string;
  questionProgressLabel: (current: number, total: number) => string;
  backLabel: string;
  continueLabel: string;
  submitLabel: string;
  closeLabel: string;
  retakeLabel: string;
  downloadCertificationLabel: string;
  passedTitle: string;
  failedTitle: string;
  scoreLabel: (scorePercent: number) => string;
  passedBody: string;
  failedBody: string;
  resolveQuestionText: (promptId: string) => string;
  resolveOptionText: (textId: string) => string;
}

export interface AssessmentOverlayProps {
  credentialId: string;
  clientId: string;
  selLocale?: string;
  copy: AssessmentOverlayCopy;
  completionCertificateUrl?: string;
  onClose: () => void;
  onPassed?: (result: SubmitAssessmentPOSTResponse) => void;
}

export const AssessmentOverlay: React.FC<AssessmentOverlayProps> = ({
  credentialId,
  clientId,
  selLocale,
  completionCertificateUrl,
  copy,
  onClose,
  onPassed,
}) => {
  const [startAssessment, { data: attempt, isLoading: isStarting, isError: isStartError }] = useStartAssessmentMutation();
  const [saveAnswer] = useSaveAnswerMutation();
  const [submitAssessment, { isLoading: isSubmitting }] = useSubmitAssessmentMutation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionByQuestion, setSelectedOptionByQuestion] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SubmitAssessmentPOSTResponse | null>(null);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const { clickMetric, viewMetric } = useMetrics();

  useEffect(() => {
    startAssessment({ credentialId, clientId, selLocale });
  }, [startAssessment, credentialId, clientId, selLocale]);

  useEffect(() => {
    if (isStartError) {
      viewMetric({
        feature: FeatureName.AssessmentError,
        metadata: { credentialId, phase: 'start' },
      });
    }
  }, [isStartError, viewMetric, credentialId]);

  useEffect(() => {
    if (attempt) {
      setSelectedOptionByQuestion(attempt.answers);
      // Resume on the first unanswered question so reopening a partially-completed attempt does
      // not restart from the beginning. If every question is answered, land on the last one.
      const firstUnansweredIndex = attempt.questions.findIndex(
        (question) => !(question.questionId in attempt.answers),
      );
      setCurrentIndex(
        firstUnansweredIndex === -1
          ? Math.max(0, attempt.questions.length - 1)
          : firstUnansweredIndex,
      );
    }
  }, [attempt]);

  const handleRetake = () => {
    setResult(null);
    setCurrentIndex(0);
    setSelectedOptionByQuestion({});
    setHasSubmitError(false);
    startAssessment({ credentialId, clientId, selLocale });
  };

  if (result) {
    const isPassed = result.outcome === 'PASSED';
    return (
      <KatModal
        visible
        onClose={onClose}
        title={copy.title}
        footer={(
          <div className="kat-group-horizontal flex justify-end gap-su-space-03">
            {isPassed ? (
              <>
                {completionCertificateUrl && (
                  <SuButton
                    variant="primary"
                    label={copy.downloadCertificationLabel}
                    icon={<span aria-hidden="true">&#8681;</span>}
                    linkProps={{
                      href: completionCertificateUrl,
                      refTag: 'su_assessment_certificate_download',
                    }}
                  />
                )}
                <SuButton variant="secondary" label={copy.closeLabel} onClick={onClose} />
              </>
            ) : (
              <>
                <SuButton variant="secondary" label={copy.closeLabel} onClick={onClose} />
                <SuButton variant="primary" label={copy.retakeLabel} onClick={handleRetake} />
              </>
            )}
          </div>
        )}
      >
        <div className="flex items-start gap-su-space-05 px-su-space-05 py-su-space-04" aria-live="polite" role="status">
          <KatProgress
            type="circular"
            size="large"
            value={result.scorePercent}
            ref={(node) => {
              if (!node) return;
              const color = isPassed ? 'var(--kat-text-success-info)' : 'var(--kat-text-status-danger)';
              const tint = isPassed ? 'var(--kat-background-status-success)' : 'var(--kat-background-status-danger)';
              const host = node as unknown as HTMLElement;
              host.style.setProperty('--kat-progress-track-foreground', color);
              host.style.setProperty('--kat-progress-value-color', color);
              host.style.setProperty('--kat-progress-value-font-weight', '700');
              host.style.setProperty('--value-color', color);
              host.style.setProperty('--value-font-weight', '700');
              host.style.setProperty('--value-font-size', '1.25rem');
              host.style.background = tint;
              host.style.borderRadius = '50%';
            }}
          />
          <div>
            <h2 className="text-su-heading-500">{isPassed ? copy.passedTitle : copy.failedTitle}</h2>
            <p className={`font-bold ${isPassed ? 'text-su-text-success-info' : 'text-su-text-status-danger'}`}>
              {copy.scoreLabel(result.scorePercent)}
            </p>
            <p>{isPassed ? copy.passedBody : copy.failedBody}</p>
          </div>
        </div>
      </KatModal>
    );
  }

  if (isStarting) {
    return (
      <KatModal visible onClose={onClose} title={copy.title}>
        <LoadingView />
      </KatModal>
    );
  }

  if (isStartError || !attempt || attempt.questions.length === 0) {
    return (
      <KatModal visible onClose={onClose} title={copy.title}>
        <ErrorView />
      </KatModal>
    );
  }

  const { attemptId, questions } = attempt;
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const selectedOptionId = selectedOptionByQuestion[currentQuestion.questionId];
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const questionProgressValue = Math.round((currentIndex / totalQuestions) * 100);

  const handleSelectOption = (questionId: string, optionId: string) => {
    setSelectedOptionByQuestion((prev) => ({ ...prev, [questionId]: optionId }));
    // Fire-and-forget autosave: a failed save is non-critical (the selection re-saves on the next
    // change and the full answer set is submitted at the end). Not unwrapped, so it never rejects.
    saveAnswer({
      credentialId, attemptId, questionId, selectedOptionId: optionId,
    });
  };

  const handleBack = () => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  };

  const handleContinue = async () => {
    if (!isLastQuestion) {
      setCurrentIndex((index) => Math.min(totalQuestions - 1, index + 1));
      return;
    }
    setHasSubmitError(false);
    try {
      const submitted = await submitAssessment({ credentialId, attemptId }).unwrap();
      clickMetric({
        feature: FeatureName.AssessmentSubmitted,
        metadata: { credentialId },
      });
      setResult(submitted);
      if (submitted.outcome === 'PASSED') {
        viewMetric({
          feature: FeatureName.AssessmentPassed,
          metadata: { credentialId, scorePercent: submitted.scorePercent },
        });
        onPassed?.(submitted);
      }
    } catch {
      setHasSubmitError(true);
      viewMetric({
        feature: FeatureName.AssessmentError,
        metadata: { credentialId, phase: 'submit' },
      });
    }
  };

  return (
    <KatModal
      visible
      onClose={onClose}
      title={copy.title}
      footer={(
        <div className="kat-group-horizontal flex justify-end gap-su-space-03">
          <SuButton
            variant="secondary"
            label={copy.backLabel}
            disabled={currentIndex === 0}
            onClick={handleBack}
          />
          <SuButton
            variant="primary"
            label={isLastQuestion ? copy.submitLabel : copy.continueLabel}
            loading={isSubmitting}
            disabled={!selectedOptionId}
            onClick={handleContinue}
          />
        </div>
      )}
    >
      <div className="flex flex-col gap-su-space-04 px-su-space-05 py-su-space-04">
        <div className="flex flex-col gap-su-space-02">
          <div className="flex items-center justify-between">
            <span className="text-su-text-secondary">
              {copy.questionProgressLabel(currentIndex + 1, totalQuestions)}
            </span>
            <span className="text-su-text-secondary">{`${questionProgressValue}%`}</span>
          </div>
          <KatProgress type="linear" value={questionProgressValue} hideValue />
        </div>
        <p className="font-bold" id={`${currentQuestion.questionId}-prompt`}>{copy.resolveQuestionText(currentQuestion.prompt)}</p>
        <div
          role="radiogroup"
          aria-labelledby={`${currentQuestion.questionId}-prompt`}
          className="flex flex-col gap-su-space-03"
        >
          {currentQuestion.options.map((option, optionIndex) => {
            const isSelected = selectedOptionId === option.optionId;
            const selectOption = () => handleSelectOption(currentQuestion.questionId, option.optionId);
            const selectedIndex = currentQuestion.options.findIndex(
              (o) => o.optionId === selectedOptionId,
            );
            const isTabStop = selectedIndex === -1 ? optionIndex === 0 : isSelected;
            const focusOptionAt = (index: number) => {
              const { length } = currentQuestion.options;
              const nextIndex = (index + length) % length;
              const nextOption = currentQuestion.options[nextIndex];
              handleSelectOption(currentQuestion.questionId, nextOption.optionId);
              optionRefs.current[nextIndex]?.focus();
            };
            return (
              <div
                key={option.optionId}
                ref={(node) => { optionRefs.current[optionIndex] = node; }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={isTabStop ? 0 : -1}
                className={`flex items-center gap-su-space-03 rounded border-su-border-width-xxsmall border-solid p-su-space-03 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-su-border-item-selected ${
                  isSelected
                    ? 'border-su-border-item-selected bg-su-background-item-selected hover:bg-su-background-item-selected-hover'
                    : 'border-su-border-item-default bg-su-background-container-content hover:bg-su-background-item-hover'
                }`}
                onClick={selectOption}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectOption();
                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    focusOptionAt(optionIndex + 1);
                  } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    focusOptionAt(optionIndex - 1);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={`size-su-space-04 shrink-0 rounded-full border-su-border-width-small border-solid ${
                    isSelected
                      ? 'border-su-border-item-selected bg-su-border-item-selected'
                      : 'border-su-border-item-default'
                  }`}
                />
                <span>{copy.resolveOptionText(option.text)}</span>
              </div>
            );
          })}
        </div>
        <div aria-live="assertive" role="alert">
          {hasSubmitError && <ErrorView />}
        </div>
      </div>
    </KatModal>
  );
};
